import { useMutation, useQueryClient } from "@tanstack/react-query";

import { mutateData } from "@/core/api/api-methods";
import {
  apiErrorHandler,
  getBackendErrorMessage,
} from "@/core/api/api-error-handler";
import {
  invalidateAfterMutation,
  walletTransactionCacheKeys,
} from "@/core/api/invalidate-after-mutation";
import { logger } from "@/core/utils/func/logger";
import { walletEndpoints } from "@/features/wallet/api/endpoints";
import type { ByReferenceRequest } from "@/features/wallet/api/dto";

/**
 * Voie PRINCIPALE du worker ledger-service (`POST
 * /Transactions/confirm-ledger-by-reference`, guide v2.5 §6.3).
 *
 * Acquitte la comptabilisation d'une transaction par sa référence
 * fonctionnelle (`FPT-YYYYMMDD-NNNNNN`), libère le blocage, met à jour les
 * soldes, passe en COMPLETED. Idempotent (garde de statut : rejeu neutre).
 * Rôles : `flashpay-service`, `flashpay-superadmin`, `flashpay-admin`.
 */
export const useConfirmLedgerByReference = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["wallet", "transactions", "confirmLedgerByReference"],
    mutationFn: (body: ByReferenceRequest) =>
      mutateData({
        url: walletEndpoints.transactionsExt.confirmLedgerByReference,
        reqBody: body as unknown as Record<string, unknown>,
        method: "POST",
      }),
    onSuccess: () =>
      invalidateAfterMutation(queryClient, walletTransactionCacheKeys()),
    onError: (error) => {
      const message = getBackendErrorMessage(error) ?? apiErrorHandler(error);
      logger.error(
        `useConfirmLedgerByReference failed: ${message}`,
        error instanceof Error ? error : undefined,
        { prefix: "wallet" }
      );
    },
  });
};
